import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PlanRow,
  ResolvedSubPlan,
  SubAttachment,
  SubBlock,
  SubPlanTier,
  SubTier,
  Worksheet,
} from '@td/shared';
import { del, get, patch, post, postForm } from '../lib/api';

const KEY = ['sub-plans'];

export interface SubPlansResponse {
  plans: ResolvedSubPlan[];
  worksheets: Worksheet[];
  attachments: SubAttachment[];
  /** The schedule the plans are laid over — the live lesson-plan rows. */
  schedule: PlanRow[];
}

export function useSubPlans() {
  return useQuery({ queryKey: KEY, queryFn: () => get<SubPlansResponse>('/sub-plans') });
}

/*
 * Every mutation refetches the whole page.
 *
 * A sub plan is one screen of tightly coupled things — edit a block and the
 * uncovered-rows list changes, delete a worksheet and three blocks stop
 * pointing at it. Reconciling that by hand in the cache would be a bug farm
 * for no gain: this page is opened a handful of times a year.
 */
function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: KEY });
}

export interface PlanPatch {
  title?: string;
  arrival?: string;
  dismissal?: string;
  notes?: string;
  blocks?: SubBlock[];
  documentIds?: string[];
}

/**
 * A plan of her own.
 *
 * `copyFrom` starts it as a copy of an existing plan rather than a blank
 * page — which is how most of these will really begin, as "the middle-of-year
 * one, but for the day of the assembly".
 */
export function useCreateSubPlan() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: {
      title: string;
      copyFrom?: { tier: SubPlanTier; day: number };
      /** One empty block per row of her schedule, rather than a blank page. */
      fromSchedule?: boolean;
    }) =>
      post<ResolvedSubPlan>('/sub-plans/plans', input),
    onSuccess: refresh,
  });
}

export function useDeleteSubPlan() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ tier, day }: { tier: SubPlanTier; day: number }) =>
      del(`/sub-plans/plans/${tier}/${day}`),
    onSuccess: refresh,
  });
}

export function useUpdateSubPlan(tier: SubPlanTier, day: number) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: PlanPatch) =>
      patch<ResolvedSubPlan>(`/sub-plans/plans/${tier}/${day}`, input),
    onSuccess: refresh,
  });
}

export function useResetSubPlan(tier: SubPlanTier, day: number) {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: () => post<ResolvedSubPlan>(`/sub-plans/plans/${tier}/${day}/reset`, {}),
    onSuccess: refresh,
  });
}

export interface WorksheetInput {
  title: string;
  tier: SubTier | null;
  subject: string;
  instructions: string;
  standards: string[];
  parts: Worksheet['parts'];
}

export function useCreateWorksheet() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: WorksheetInput) => post<Worksheet>('/sub-plans/worksheets', input),
    onSuccess: refresh,
  });
}

export function useUpdateWorksheet() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, ...input }: Partial<WorksheetInput> & { id: string }) =>
      patch<Worksheet>(`/sub-plans/worksheets/${id}`, input),
    onSuccess: refresh,
  });
}

export function useDeleteWorksheet() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => del(`/sub-plans/worksheets/${id}`),
    onSuccess: refresh,
  });
}

export function useAddLink() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: { name: string; note: string; url: string }) =>
      post<SubAttachment>('/sub-plans/attachments/link', input),
    onSuccess: refresh,
  });
}

export function useUploadAttachment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ file, name, note }: { file: File; name: string; note: string }) => {
      const body = new FormData();
      body.append('file', file);
      body.append('name', name);
      body.append('note', note);
      return postForm<SubAttachment>('/sub-plans/attachments/file', body);
    },
    onSuccess: refresh,
  });
}

export function useDeleteAttachment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => del(`/sub-plans/attachments/${id}`),
    onSuccess: refresh,
  });
}
